import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  delay,
  map,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { OnboardingAPI } from '../../../data/mo-api/onboarding.api';

import { APP_CONSTANTS as CONST } from '@app/app.constants';
import { Store } from '@ngrx/store';
import { interval, of } from 'rxjs';
import { EAUTH10TIX_STATUS, EONBOARDING_STEPS } from '../../../core/enums/onboarding.enum';
import { IOnboardingKYC, IOnboardingSession } from '../../../core/models/onboarding.model';
import { HandleTokenService } from '../../../core/services/handle-token.service';
import { AppFacade } from '../../../facades/app.facade';
import { ErrorFacade } from '../../../facades/error.facade';
import { OnboardingFacade } from '../../../facades/onboarding.facade';
import { MapErrorService } from '../../services/error/map-error.service';

import { EERROR_PAGE_TYPES, IErrorComponentData } from '@app/core/models/error.model';
import { EVENTS } from '@app/data/events/events-by-category';
import {
  ERROR_COMPONENT_CONSTANTS,
  ERROR_CONSTANTS,
} from '@app/domain/services/error/error.constants';
import { UtilService } from '@app/services/util.service';
import * as appSelectors from '@store/app/app.selectors';
import * as onboardingActions from '@store/onboarding/onboarding.actions';
import * as onboardingSelectors from '@store/onboarding/onboarding.selectors';
import { IOnboardingParamsWithoutFormat } from '../../../core/models/onboarding.model';
import { CustomerAPI } from '../../../data/mo-api/customer.api';
import { OnboardingService } from '../../services/onboarding/onboarding.service';
import { ICustomerValidate } from '@app/core/models/customer.model';
import { IPutRecord, IExtraData } from '@app/core/models/app.model';
import { CustomerFacade } from '@app/facades/customer.facade';
import { APP_ROUTES as ROUTES } from '@app/app.routes';

@Injectable()
export class OnboardingEffects {
  public AUTH10TIX_STEP = EONBOARDING_STEPS.AUTH10TIX;
  public AUTH10TIX_STATUS = EAUTH10TIX_STATUS;

  constructor(
    private actions$: Actions,
    private _onboardingAPI: OnboardingAPI,
    private _customerAPI: CustomerAPI,
    private _onboardingFacade: OnboardingFacade,
    private _appFacade: AppFacade,
    private _errorFacade: ErrorFacade,
    private _customerFacade: CustomerFacade,
    private _onboardingService: OnboardingService,
    private _handleTokenService: HandleTokenService,
    private _mapErrorService: MapErrorService,
    private _utilService: UtilService,
    private _store: Store
  ) {}

  public requestOnboardingSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.requestOnboardingSession),
      switchMap(({ step }) =>
        this._onboardingAPI.requestOnboardingSession(step).pipe(
          map((information: IOnboardingSession) =>
            onboardingActions.successRequestOnboardingSession({ information })
          ),
          catchError((error) =>
            of(
              onboardingActions.failureRequestOnboardingSession({
                message: error,
              })
            )
          )
        )
      )
    )
  );

  public failureRequestOnboardingSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureRequestOnboardingSession),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public getPreCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.getPreCustomer),
      switchMap((_) =>
        this._onboardingAPI.getPreCustomer().pipe(
          map((stepInfo: IOnboardingParamsWithoutFormat) => {
            return onboardingActions.successGetPreCustomer({
              stepInfo: this._onboardingService.buildCustomerInfo(stepInfo),
            });
          }),
          catchError((error) => of(onboardingActions.failureGetPreCustomer({ message: error })))
        )
      )
    )
  );

  public saveOnboardingStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.saveOnboardingStep),
      withLatestFrom(
        this._store.select(onboardingSelectors.selectCaptchaDNIForm),
        this._store.select(onboardingSelectors.selectOnboardingStep),
        this._store.select(appSelectors.selectFastOnboardingFlag),
        this._store.select(appSelectors.selectTransactions),
        this._store.select(appSelectors.selectSessionId)
      ),
      switchMap(
        ([{ stepInfo }, token, onboardingStep, fast_onboarding, transaction, sessionId]) => {
          const step_onboarding = onboardingStep.toString();
          let updatedStepInfo = { ...stepInfo, step_onboarding, fast_onboarding };
          return this._onboardingAPI
            .savePreCustomer(updatedStepInfo, token, transaction?.id, sessionId)
            .pipe(
              map((_) => {
                // this._onboardingFacade.setNextOnboardingStep();
                return onboardingActions.successSaveOnboardingStep();
              }),
              catchError((error) =>
                of(
                  onboardingActions.failureSaveOnboardingStep({
                    message: error,
                    formData: stepInfo,
                  })
                )
              )
            );
        }
      )
    )
  );

  public successSaveOnboardingStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.successSaveOnboardingStep),
      withLatestFrom(this._store.select(appSelectors.selectTransactions)),
      switchMap(([_, transactions]) =>
        this._customerAPI.requestCustomerValidate(transactions?.id).pipe(
          map((_) => onboardingActions.successUpgradeCustomer()),
          catchError((error) => of(onboardingActions.failureUpgradeCustomer({ message: error })))
        )
      )
    )
  );

  public failureSaveOnboardingStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureSaveOnboardingStep),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          if (messageError === ERROR_CONSTANTS.ERRORS.MESSAGE_FRONTEND.CUSTOMER_EXISTS) {
            this._onboardingFacade.setCustomerExists(true);
            this._onboardingFacade.setCustomerBlockedByMultipleAttempts(false);
          }

          if (
            messageError ===
            ERROR_CONSTANTS.ERRORS.MESSAGE_FRONTEND.CUSTOMER_DISABLED_MULTIPLE_ATTEMPTS
          ) {
            this._onboardingFacade.setCustomerExists(false);
            this._onboardingFacade.setCustomerBlockedByMultipleAttempts(true);
          }
        })
      ),
    { dispatch: false }
  );

  public failureUpgradeCustomer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureUpgradeCustomer),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public successUpgradeCustomer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.successUpgradeCustomer),
        withLatestFrom(
          this._store.select(appSelectors.selectFastOnboardingFlag),
          this._store.select(appSelectors.selectPurchaseLessOnboardingFlag)
        ),
        tap(([_, isFastOnboarding, isPurchaselessOnboarding]) => {
          if (!isFastOnboarding) this._appFacade.nextStep();
          const event = EVENTS.KYC.VISIT;
          const dataFingerPrint = this._utilService.getFingerPrintData();
          const previousRoute: string = !!isFastOnboarding
            ? ROUTES.FAST_ONBOARDING_STEP_1
            : ROUTES.ONBOARDING;
          this._appFacade.setPreviousRoute(previousRoute);
          const route: string = ROUTES.VALIDATIONS;
          this._appFacade.sendPutRecord(event);
          //TODO: temporal fix while defining purchaseless onboarding behaviour
          if (!isPurchaselessOnboarding) this._appFacade.setDataFingerPint(dataFingerPrint);
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public saveSecondOnboardingStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.saveSecondOnboardingStep),
      withLatestFrom(
        this._store.select(onboardingSelectors.selectOnboardingStep),
        this._store.select(appSelectors.selectFastOnboardingFlag),
        this._store.select(appSelectors.selectTransactions),
        this._store.select(appSelectors.selectSessionId)
      ),
      switchMap(([{ stepTwoInfo }, onboardingStep, fast_onboarding, transaction, sessionId]) => {
        const step_onboarding = onboardingStep.toString();
        let updatedStepTwoInfo = { ...stepTwoInfo, step_onboarding, fast_onboarding };
        return this._onboardingAPI
          .savePreCustomer(updatedStepTwoInfo, null, transaction?.id, sessionId)
          .pipe(
            map((_) => {
              return onboardingActions.successSaveSecondOnboardingStep();
            }),
            catchError((error) =>
              of(
                onboardingActions.failureSaveSecondOnboardingStep({
                  message: error,
                  secondFormData: stepTwoInfo,
                })
              )
            )
          );
      })
    )
  );

  public successSaveSecondOnboardingStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.successSaveSecondOnboardingStep),
        withLatestFrom(
          this._store.select(appSelectors.selectStep),
          this._store.select(appSelectors.selectPurchaseLessOnboardingFlag)
        ),
        tap(([_, step, purchaselessOnboarding]) => {
          let route: string;
          this._appFacade.setCustomer(true);
          if (!purchaselessOnboarding) {
            this._handleTokenService.setUserStep(step);
            route = ROUTES.TRANSACTION_VALIDATE;
          } else {
            route = ROUTES.PAYMENT_METHOD;
            this._appFacade.setNewStep(EONBOARDING_STEPS.PAYMENT_METHOD);
          }
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public failureSaveSecondOnboardingStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureSaveSecondOnboardingStep),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          if (messageError === ERROR_CONSTANTS.ERRORS.MESSAGE_FRONTEND.CUSTOMER_EXISTS) {
            this._onboardingFacade.setCustomerExists(true);
            this._onboardingFacade.setCustomerBlockedByMultipleAttempts(false);
          }

          if (
            messageError ===
            ERROR_CONSTANTS.ERRORS.MESSAGE_FRONTEND.CUSTOMER_DISABLED_MULTIPLE_ATTEMPTS
          ) {
            this._onboardingFacade.setCustomerExists(false);
            this._onboardingFacade.setCustomerBlockedByMultipleAttempts(true);
          }
        })
      ),
    { dispatch: false }
  );

  public saveAuth10tixStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.saveAuth10tixStatus),
      withLatestFrom(this._store.select(appSelectors.selectStep)),
      switchMap(([_, step]) =>
        this._onboardingAPI.saveOnboardingStep(step, {}).pipe(
          map((_) => onboardingActions.successSaveAuth10tixStatus()),
          catchError((error) =>
            of(onboardingActions.failureSaveAuth10tixStatus({ message: error }))
          )
        )
      )
    )
  );

  public failureSaveAuth10tixStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureSaveAuth10tixStatus),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public requestAuth10tixStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.requestAuth10tixStatus),
      switchMap((_) =>
        this._onboardingAPI.requestOnboardingSession(this.AUTH10TIX_STEP).pipe(
          map((information: IOnboardingSession) => {
            const { data } = information;
            return onboardingActions.successRequestAuth10tixStatus({
              auth10tix: data,
            });
          }),
          catchError((error) =>
            of(
              onboardingActions.failureRequestAuth10tixStatus({
                message: error,
              })
            )
          )
        )
      )
    )
  );

  public failureRequestAuth10tixStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureRequestAuth10tixStatus),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public listentAuth10tixStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.listentAuth10tixStatus),
        delay(3000),
        tap((_) => {
          return interval(CONST.LISTEN_KYC_STATUS)
            .pipe(
              mergeMap(() => this._onboardingAPI.requestKYCStatus(this.AUTH10TIX_STEP)),
              tap((auth10tix: IOnboardingKYC) => {
                const { status } = auth10tix;
                return status !== this.AUTH10TIX_STATUS.UNDER_REVIEW
                  ? this._onboardingFacade.successAuth10tixStatuss(auth10tix)
                  : false;
              }),
              takeWhile(({ status }) => status === this.AUTH10TIX_STATUS.UNDER_REVIEW)
            )
            .subscribe();
        })
      ),
    { dispatch: false }
  );

  public successAuth10tixStatuss$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.successRequestAuth10tixStatus),
        tap((_) => {
          this._appFacade.setIsLoading();
        })
      ),
    { dispatch: false }
  );

  // DEMO
  public setUnderReviewAuth10tixStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.setUnderReviewAuth10tixStatus),
      delay(10000),
      map((_) =>
        onboardingActions.setApprovedAuth10tixStatus({
          status: this.AUTH10TIX_STATUS.APPROVED,
        })
      )
    )
  );
  // DEMO
}
